import Cores from '@constants/Color'
import { WITHOUT_PORTFOLIO } from '@constants/Text'
import { aawzDate } from '@utils/dates/aawz-date'
import { getRandomColor } from '@utils/Format'
import {
  CLIENT_BENCHMARK_KEY,
  BENCHMARK_PERSONALIZADO_COLOR,
} from '@utils/clienteBenchmark'
import { resolveGroupLabel } from '@modules/consolidator/utils/resolve-group-label/resolve-group-label'
import { BACKEND_NO_PORTFOLIO_KEY } from '@modules/consolidator/utils/adapt-wallet-analysis-response/adapt-wallet-analysis-response'

export const getAvailableBenchmarkNames = (adapted) => {
  const names = new Set()
  if (!adapted || !adapted.ok || !adapted.portfolio_series) return names
  const byName = adapted.portfolio_series.benchmarks || {}
  for (const nome of Object.keys(byName)) {
    const data = byName[nome]
    if (Array.isArray(data) && data.length > 0) names.add(nome)
  }
  return names
}

export const formatRentabilityChart = (
  adapted,
  {
    primaryColor,
    getBankColor,
    getCorretoraSimple,
    groupBy = null,
    groupLabelByKey = null,
    selectedBenchmarks = [],
    clienteBenchmarkMeta = null,
    isClienteBenchmarkSelected = true,
  } = {}
) => {
  if (!adapted || !adapted.ok || !adapted.portfolio_series) {
    return cloneEmpty()
  }

  const series = adapted.portfolio_series
  const breakdown = (adapted.breakdown && adapted.breakdown.data) || {}

  const labels = (Array.isArray(series.index) ? series.index : []).map(isoToBR)
  const corretoras = Object.keys(breakdown)
  const consolidadoData = arrayOrEmpty(series.rentabilidade)
  const isOffshoreMode =
    !!(adapted.filters && adapted.filters.tipo_investimento === 'offshore')
  const singleGroupOverride = resolveSingleGroupOverride({
    corretoras,
    isOffshoreMode,
  })

  const datasets = [
    buildConsolidadoDataset({
      data: consolidadoData,
      primaryColor,
      label: singleGroupOverride.label,
    }),
    ...corretoras
      .filter((corretora) => !singleGroupOverride.skipNames.has(corretora))
      .map((corretora) =>
        buildCorretoraDataset({
          legend: corretora,
          data: arrayOrEmpty(breakdown[corretora].rentabilidade),
          getBankColor,
          getCorretoraSimple,
          groupBy,
          groupLabelByKey,
        })
      ),
  ]

  const benchmarksDatasets = buildBenchmarksDatasets({
    benchmarksByName: series.benchmarks || {},
    selectedBenchmarks,
  })
  const clienteBenchmarkDataset = isClienteBenchmarkSelected
    ? buildClienteBenchmarkDataset({
        benchmarksByName: series.benchmarks || {},
        clienteBenchmarkMeta,
      })
    : null
  return appendBenchmarks(
    { labels, datasets },
    clienteBenchmarkDataset
      ? [...benchmarksDatasets, clienteBenchmarkDataset]
      : benchmarksDatasets
  )
}

export const filterChartToConsolidatedOnly = (chart) => {
  if (!chart || !Array.isArray(chart.datasets)) return chart
  return {
    ...chart,
    datasets: chart.datasets.filter((dataset) => dataset._kind !== 'group'),
  }
}

const buildBenchmarksDatasets = ({ benchmarksByName, selectedBenchmarks }) => {
  const list = Array.isArray(selectedBenchmarks) ? selectedBenchmarks : []
  return list
    .filter(
      (bench) =>
        bench &&
        bench.nome &&
        Array.isArray(benchmarksByName[bench.nome]) &&
        benchmarksByName[bench.nome].length > 0
    )
    .map((bench) =>
      buildBenchmarkDataset({
        bench,
        data: benchmarksByName[bench.nome],
      })
    )
}

const buildClienteBenchmarkDataset = ({
  benchmarksByName,
  clienteBenchmarkMeta,
}) => {
  if (!clienteBenchmarkMeta) return null
  const data = benchmarksByName[CLIENT_BENCHMARK_KEY]
  if (!Array.isArray(data) || data.length === 0) return null
  const label = clienteBenchmarkMeta.label || 'Meu Benchmark'
  return {
    label,
    data,
    borderColor: BENCHMARK_PERSONALIZADO_COLOR,
    backgroundColor: BENCHMARK_PERSONALIZADO_COLOR,
    fill: false,
    stepped: true,
    pointRadius: 0,
    borderWidth: 3,
    line: { borderDash: [0, 10], borderCapStyle: 'round' },
    _kind: 'clienteBenchmark',
  }
}

const buildBenchmarkDataset = ({ bench, data }) => {
  const BORDER_WIDTH = 3
  const POINT_RADIUS = 0
  const BORDER_DASH = [0, 10]
  const color = Cores.BENCHMARKS_COLORS[bench.nome] || getRandomColor()
  return {
    label: bench.nome,
    data,
    borderColor: color,
    backgroundColor: color,
    fill: false,
    stepped: true,
    pointRadius: POINT_RADIUS,
    borderWidth: BORDER_WIDTH,
    line: { borderDash: BORDER_DASH, borderCapStyle: 'round' },
    _kind: 'benchmark',
  }
}

const appendBenchmarks = (chart, benchmarksDatasets) => {
  const list = Array.isArray(benchmarksDatasets) ? benchmarksDatasets : []
  if (!list.length) return chart
  return {
    ...chart,
    datasets: [...(chart.datasets || []), ...list],
  }
}

const cloneEmpty = () => ({ labels: [], datasets: [] })

const arrayOrEmpty = (value) => (Array.isArray(value) ? value : [])

const isoToBR = (iso) => aawzDate(iso, 'YYYY-MM-DD').format('DD/MM/YYYY')

const corretoraKey = (legend, getCorretoraSimple) =>
  typeof getCorretoraSimple === 'function' ? getCorretoraSimple(legend) : legend

const resolveDisplayLabel = (
  legend,
  getCorretoraSimple,
  groupBy,
  groupLabelByKey
) => {
  if (legend === BACKEND_NO_PORTFOLIO_KEY && groupBy === 'nome_portfolio') {
    return WITHOUT_PORTFOLIO
  }
  const overrideLabel = resolveGroupLabel(legend, groupLabelByKey)
  if (overrideLabel) return overrideLabel
  return corretoraKey(legend, getCorretoraSimple)
}

const CONSOLIDADO_DEFAULT_LABEL = 'Consolidado'

const buildConsolidadoDataset = ({ data, primaryColor, label }) => ({
  label: label || CONSOLIDADO_DEFAULT_LABEL,
  fill: false,
  borderColor: primaryColor,
  backgroundColor: primaryColor,
  borderWidth: 3,
  data,
  _kind: 'consolidado',
})

const resolveSingleGroupOverride = ({ corretoras, isOffshoreMode }) => {
  if (isOffshoreMode && corretoras.length === 1) {
    const nome = corretoras[0]
    return { label: nome, skipNames: new Set([nome]) }
  }
  return {
    label: CONSOLIDADO_DEFAULT_LABEL,
    skipNames: new Set(),
  }
}

const buildCorretoraDataset = ({
  legend,
  data,
  getBankColor,
  getCorretoraSimple,
  groupBy,
  groupLabelByKey,
}) => {
  const color = typeof getBankColor === 'function' ? getBankColor(legend) : null
  return {
    label: resolveDisplayLabel(
      legend,
      getCorretoraSimple,
      groupBy,
      groupLabelByKey
    ),
    fill: false,
    stepped: true,
    borderWidth: 3,
    borderColor: color,
    backgroundColor: color,
    data,
    line: { borderDash: [0, 10], borderCapStyle: 'round' },
    _kind: 'group',
  }
}

